import { useEffect } from 'react'

import { useAuth } from '@/lib/config/auth-provider'
import { setReferralCookies } from '@/lib/utils/referral-cookie'
import { useRouter, useSearchParams } from '@/routing/navigation'

/**
 * Referral attribution capture — extracted from `HomePage.tsx` (Assistant MVP Step 12,
 * docs/plans/assistant-mvp-plan.md, D1d).
 *
 * `HomePage` used to be the only thing mounted at `/`, so its own `useEffect` reading
 * `?ref=` and redirecting to `/register` was, in effect, "the site's referral capture."
 * Step 11 put `HomeLabPage` at `/` instead, and `HomeLabPage` never carried an
 * equivalent — so a `?ref=` link landing on `/` silently stopped attributing. D1d is
 * explicit that the fix is a rung-2 EXTRACTION (lift it out, both entry points use the
 * lifted copy), not a rung-4 copy into `HomeLabPage` — two referral paths would diverge.
 *
 * This hook is that extraction, byte-identical in behaviour to the original effect:
 *   - Does nothing while auth is still resolving.
 *   - Does nothing for an authenticated visitor (no redirect, no cookie) — being logged
 *     in and simply browsing with a stray `?ref=` in the URL must not hijack navigation.
 *   - Does nothing when there is no `?ref=` param.
 *   - Otherwise: writes the referral cookie (`setReferralCookies`, source
 *     `'referral_link'`) and replaces the current entry with `/register?ref=...`.
 *
 * Mounted once per entry point that should attribute referrals — today that is
 * `HomeEntryLayout` in `AppRoutes.tsx`, wrapping `/`, `ROUTES.homeLab` and
 * `ROUTES.homeClassic` — rather than being called from inside `HomePage` or
 * `HomeLabPage` themselves, so the behaviour does not depend on which page component
 * happens to render there and cannot fork into two copies again.
 */
export function useReferralCapture(): void {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const searchParams = useSearchParams()
  const router = useRouter()
  const refCode = searchParams.get('ref')

  useEffect(() => {
    if (!authLoading && !isAuthenticated && refCode) {
      setReferralCookies(refCode, 'referral_link')
      router.replace(`/register?ref=${encodeURIComponent(refCode)}`)
    }
  }, [authLoading, isAuthenticated, refCode, router])
}
